<!-- Copyright 2024, Common Good Learning Tools LLC -->
<template><k-dialog v-model="dialog_open" max-width="1200" :fullscreen="$vuetify.breakpoint.smAndDown" :persistent="!pop_up_closable" :transition="false" overlay-opacity="0.9" scrollable @click:outside="clicked_outside" style="border-radius:16px" :content-class="$vuetify.breakpoint.smAndDown?'k-home-dialog-card-small':''">
<k-card class="k-home-dialog-card">
	<k-card-title>
		<div style="width: 100%;">
			<div class="d-flex align-center">
				<div v-if="header_icon" style="width:44px; height:44px; text-align:center; padding-top:6px;background-color:#000; border-radius:36px;">
					<k-icon style="color:#fff;">{{ header_icon }}</k-icon>
				</div>

				<k-spacer v-if="subdomain_data"/>
				<h3 v-if="subdomain_data" class="pink--text text--darken-4">{{site_config.app_name}} for {{subdomain_data.agency_name}}</h3>
				<k-spacer/>

				<div>
					<div v-if="pop_up_closable && button">
						<k-btn v-if="button.icon" icon small @click="get_started_clicked">
							<k-icon>{{ button.text }}</k-icon>
						</k-btn>
						<k-btn v-else color="primary" class="xk-tight-btn" @click="get_started_clicked">
							{{ button.text }}
							<k-icon small class="ml-2">fas fa-arrow-right</k-icon>
						</k-btn>
					</div>

					<k-btn v-else color="primary" class="xk-tight-btn" @click="get_started_clicked">
						Sign In to {{ site_config.app_name }}
						<k-icon small class="ml-2">fas fa-sign-in</k-icon>
					</k-btn>
				</div>
			</div>

			<!-- home_header_html can be set in config file -->
			<div v-if="site_config.home_header_html" v-html="accessible_html(site_config.home_header_html, 'header')"></div>
		</div>
	</k-card-title>

	<k-card-text>
		<div class="k-home-dialog-wrapper-inner">
			<div class="fr-view" v-html="accessible_html(pop_up_content, 'content')"></div>

			<div v-if="!backdoor_login_only" style="clear:both; border-top:1px solid #ccc; margin-top:20px;">
				<!-- this would show the user's info (e.g. email and name); since the default will now be that we show the dialog in addition to the home page, we shouldn't need this here anymore -->
				<!-- <div v-if="oidc_info" class="mt-3 pt-2 mb-1" style="font-size:16px;" v-html="oidc_info"></div> -->
				<div v-if="!signed_in" class="text-center mt-4 mb-2">
					<k-btn v-if="pop_up_closable" color="secondary" @click="sign_in">Sign In to {{ site_config.app_name }}<k-icon small class="ml-2">fas fa-sign-in</k-icon></k-btn>
					<k-btn v-if="show_guest_mode_btn" color="secondary" @click="open_in_guest_mode">Open {{ site_config.app_name }} in “Guest Mode”</k-btn>
				</div>
			</div>

			<!-- site_footer_html can be set in config file -->
			<div v-if="site_config.site_footer_html" v-html="accessible_html(site_config.site_footer_html, 'footer')"></div>
		</div>
	</k-card-text>
</k-card>
</k-dialog></template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
	props: {
		pop_up_closable: { type: Boolean, required: true },
	},

	data() { return {
		dialog_open: true,
		// add to includer component
	}},

	computed: {
		...mapState(['user_info', 'site_config', 'subdomain_data']),

		...mapGetters(['signed_in', 'role', 'backdoor_login_only']),

		show_guest_mode_btn() {
			// if pop_up_closable is false (which will never be the case if the user is signed in) and the user isn't *required* to sign in, show a "open in guest mode" btn
			return !this.pop_up_closable && this.site_config.require_sign_in != 'yes'
		},

		header_icon() {
			return this.site_config.pop_up?.header_icon || false
		},

		button() {
			return this.site_config.pop_up?.close_button
		},

		home_page_variant() {
			// if we have a pop_up variant, return that
			if (this.site_config.home_page_variants.includes('pop_up')) return 'pop_up'

			// if we only have one variant, return it
			if (this.site_config.home_page_variants.length == 1) {
				return this.site_config.home_page_variants[0]
			}

			// if not signed in and we have an unsignedin variant, return it
			if (!this.signed_in && this.site_config.home_page_variants.includes('unsignedin')) return 'unsignedin'

			// else try to return varient matching role, but if we don't have a student or parent varient, just return 'staff', which will serve for 'signedin'
			if (this.role == 'student' && this.site_config.home_page_variants.includes('student')) return 'student'
			if (this.role == 'parent' && this.site_config.home_page_variants.includes('parent')) return 'parent'	// in this case you should be viewing FamilyHome...
			return 'staff'
		},
		
		pop_up_content() {
			this.$store.dispatch('get_home_page_content', this.home_page_variant)
			return this.$store.state.home_page_content[this.home_page_variant]
		},

		oidc_info() {
			let s = ''
			if (this.user_info.first_name) s += sr('<div>You are signed in as $1 $2 ($3)</div>', this.user_info.first_name, this.user_info.last_name, this.user_info.email)
			
			if (this.site_config.show_user_system_data_on_home == 'yes') {
				s += '<ul class="ml-4">'
				if (this.user_info.oidc_data?.system_name) s += sr('<li>School system: $1</li>', this.user_info.oidc_data.system_name)
				if (this.user_info.oidc_data?.school_name) s += sr('<li>School: $1</li>', this.user_info.oidc_data.school_name)
				if (this.user_info.oidc_data?.user_role) s += sr('<li>Role: $1</li>', this.user_info.oidc_data.user_role)
				if (this.user_info.first_name) s += '</ul>'
			}
			return s
		}
	},
	methods: {
		accessible_html(html, scope) {
			if (empty(html)) return ''
			html = html.replace(/%3A/g, ':')
			html = html.replace(/%24/g, '$')
			return this.add_fallback_alt_text(html, scope)
		},

		add_fallback_alt_text(html, scope='content') {
			if (!html) return html
			if (typeof DOMParser == 'undefined') return html

			let parser = new DOMParser()
			let doc = parser.parseFromString(`<div>${html}</div>`, 'text/html')
			let wrapper = doc.body.firstElementChild
			if (!wrapper) return html

			let img_count = 0
			for (let img of wrapper.querySelectorAll('img')) {
				if (img.hasAttribute('alt')) continue
				img_count += 1
				img.setAttribute('alt', this.infer_image_alt(img, img_count, scope))
			}

			this.normalize_inline_text_contrast(wrapper, scope)

			return wrapper.innerHTML
		},

		normalize_inline_text_contrast(wrapper, scope='content') {
			if (scope != 'content') return

			for (let el of wrapper.querySelectorAll('[style]')) {
				let color = this.get_inline_text_color(el.getAttribute('style'))
				if (!color) continue

				let normalized_color = this.normalize_text_color_for_white_background(color)
				if (!normalized_color) continue

				el.style.color = normalized_color
			}
		},

		get_inline_text_color(style_text='') {
			let match = style_text.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i)
			return match ? match[1].trim() : null
		},

		normalize_text_color_for_white_background(color_text) {
			let rgb = this.parse_css_color(color_text)
			if (!rgb) return null

			const white = {r:255, g:255, b:255}
			if (this.get_contrast_ratio(rgb, white) >= 4.5) return null

			let adjusted = {...rgb}
			let iterations = 0
			while (this.get_contrast_ratio(adjusted, white) < 4.5 && iterations < 24) {
				adjusted = {
					r: Math.max(0, Math.round(adjusted.r * 0.82)),
					g: Math.max(0, Math.round(adjusted.g * 0.82)),
					b: Math.max(0, Math.round(adjusted.b * 0.82)),
				}
				iterations += 1
			}

			if (this.get_contrast_ratio(adjusted, white) < 4.5) adjusted = {r:68, g:68, b:68}
			return this.rgb_to_hex(adjusted)
		},

		parse_css_color(color_text) {
			if (typeof document == 'undefined' || typeof window == 'undefined') return null

			let probe = document.createElement('span')
			probe.style.color = ''
			probe.style.color = color_text
			if (!probe.style.color) return null

			probe.style.display = 'none'
			document.body.appendChild(probe)
			let resolved = window.getComputedStyle(probe).color
			probe.remove()

			let match = resolved.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i)
			if (!match) return null

			return {
				r: Number(match[1]),
				g: Number(match[2]),
				b: Number(match[3]),
			}
		},

		get_relative_luminance({r, g, b}) {
			let normalized = [r, g, b].map((channel) => {
				channel /= 255
				return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4)
			})
			return (0.2126 * normalized[0]) + (0.7152 * normalized[1]) + (0.0722 * normalized[2])
		},

		get_contrast_ratio(color_1, color_2) {
			let luminance_1 = this.get_relative_luminance(color_1)
			let luminance_2 = this.get_relative_luminance(color_2)
			let brightest = Math.max(luminance_1, luminance_2)
			let darkest = Math.min(luminance_1, luminance_2)
			return (brightest + 0.05) / (darkest + 0.05)
		},

		rgb_to_hex({r, g, b}) {
			return '#' + [r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')
		},

		infer_image_alt(img, img_count, scope) {
			let src = (img.getAttribute('src') || '').toLowerCase()
			let href = (img.closest('a')?.getAttribute('href') || '').toLowerCase()
			let combined = `${src} ${href}`

			if (combined.includes('facebook')) return 'Facebook'
			if (combined.includes('instagram')) return 'Instagram'
			if (combined.includes('twitter') || combined.includes('x.com')) return 'X'
			if (combined.includes('youtube')) return 'YouTube'
			if (combined.includes('linkedin')) return 'LinkedIn'

			if (scope == 'header') return `${this.site_config.app_name} header image`
			if (img_count == 1) return `${this.site_config.app_name} branded image`
			return `${this.site_config.app_name} about image`
		},

		clicked_outside() {
			// if pop_up_closable is false, the dialog can't be closed
			if (!this.pop_up_closable) return
			this.$emit('dialog_cancel')
		},

		get_started_clicked() {
			// if they click the get started button and pop_up_closable is false, send them to the sign in screen
			if (!this.pop_up_closable) this.sign_in()
			// else close the dialog
			this.$emit('dialog_cancel')
		},

		open_in_guest_mode() {
			// if they click the guest mode btn, tell vapp to go to main
			this.$emit('dialog_cancel', 'main')
		},

		sign_in($evt) {
			this.$emit('dialog_cancel')
			// if user holds down shift and cmd (mac, prob. alt on windows), always show native cureum login; this allows, e.g., for us to sign in using the magic password to Inspire
			if ($evt && $evt.shiftKey && $evt.metaKey) {
				vapp.app_mode = 'login'
			} else {
				vapp.redirect_to_login()
			}
		},
	}
}
</script>

<style lang="scss">
.k-home-dialog-card {
	border-radius:16px!important;
	width:100%;
	overflow:auto;
}

.k-home-dialog-wrapper-inner {
	background-color:#fff;
	padding: 0 16px;

	.fr-view {
		font-size:18px;
		line-height:1.4em;
		max-width:800px;
		margin: 0 auto;
		text-align:left;

		h2 {
			font-size:27px;
			line-height:33px;
		}
		h3 {
			font-size:22px;
			line-height:27px;
		}
	}

}

.k-home-dialog-split-content {
	display:flex;
	margin:16px 12px 0 12px;
	line-height:1.4em;
}

.k-home-dialog-split-content-left {
	flex:0 0 50%;
	margin-right:12px;
	padding-top:8px;
}

.k-home-dialog-split-content-right {
	flex:0 0 50%; 
	border-left:1px solid #ccc;
	padding-left:12px;
	padding-top:8px;
}

.k-home-dialog-card-small {
	border-radius:0!important;

	.k-home-dialog-card {
		border-radius:0!important;
	}

	.k-home-dialog-social-btns { 
		visibility:hidden; 
	}

	.k-home-dialog-footer {
		border-radius:0!important;
	}

	.k-home-dialog-split-content {
		display:block;
		margin:0;
	}

	.k-home-dialog-split-content-left {
		margin-right:0;
		padding-top:0px;
	}

	.k-home-dialog-split-content-right { 
		border-left:0;
		padding-left:0px;
		padding-top:0px;
		border-top:1px solid #666;
		margin-top:8px;
	}
}
</style>
